import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useFitStore } from '../store/useFitStore';
import type { Program, Workout, Exercise, GlobalExercise } from '../store/types';
import Button from '../components/shared/Button';
import Card from '../components/shared/Card';
import Input from '../components/shared/Input';

const ProgramEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { masterPrograms, updateMasterProgram, addMasterProgram, globalExercises, showToast } = useFitStore();

  const [program, setProgram] = useState<Program>({
    id: id === 'new' ? crypto.randomUUID() : id || '',
    title: '',
    workouts: [],
    coachId: '',
    isLocked: false
  });

  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeWorkoutId, setActiveWorkoutId] = useState<string | null>(null);

  useEffect(() => {
    if (id !== 'new') {
      const existing = masterPrograms.find(p => p.id === id);
      if (existing) setProgram(existing);
    }
  }, [id, masterPrograms]);

  const handleSave = async () => {
    if (!program.title) {
      showToast('Please enter a program title', 'error');
      return;
    }
    setIsSaving(true);
    try {
      if (id === 'new') {
        await addMasterProgram(program);
        showToast('Program created successfully!');
      } else {
        await updateMasterProgram(program);
        showToast('Program updated!');
      }
      navigate('/coach');
    } catch (err: any) {
      showToast(`Error saving: ${err.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const addWorkout = () => {
    if (program.isLocked) return;
    const newWorkout: Workout = {
      id: `w_${Date.now()}`,
      title: 'New Workout Day',
      exercises: []
    };
    setProgram(prev => ({ ...prev, workouts: [...prev.workouts, newWorkout] }));
  };

  const snapExercise = (workoutId: string, globalEx: GlobalExercise) => {
    if (program.isLocked) return;
    const newExercise: Exercise = {
      id: `e_${Date.now()}`,
      name: globalEx.name,
      sets: globalEx.defaultSets,
      reps: globalEx.defaultReps,
      restSeconds: globalEx.defaultRest,
      videoUrl: globalEx.videoDemoUrl
    };
    
    setProgram(prev => ({
      ...prev,
      workouts: prev.workouts.map(w => 
        w.id === workoutId ? { ...w, exercises: [...w.exercises, newExercise] } : w
      )
    }));
    setSearchQuery('');
    setActiveWorkoutId(null);
    showToast(`${globalEx.name} added`);
  };

  const updateExercise = (workoutId: string, exerciseId: string, updates: Partial<Exercise>) => {
    setProgram(prev => ({
      ...prev,
      workouts: prev.workouts.map(w => 
        w.id === workoutId ? {
          ...w,
          exercises: w.exercises.map(e => {
            if (e.id === exerciseId) {
              if (program.isLocked && (updates.name || updates.sets || updates.restSeconds)) return e;
              return { ...e, ...updates };
            }
            return e;
          })
        } : w
      )
    }));
  };

  const removeExercise = (workoutId: string, exerciseId: string) => {
    if (program.isLocked) return;
    setProgram(prev => ({
      ...prev,
      workouts: prev.workouts.map(w => 
        w.id === workoutId ? {
          ...w,
          exercises: w.exercises.filter(e => e.id !== exerciseId)
        } : w
      )
    }));
  };

  const toggleSuperset = (workoutId: string, exerciseIndex: number) => {
    if (program.isLocked) return;
    setProgram(prev => {
      const newWorkouts = prev.workouts.map(w => {
        if (w.id !== workoutId) return w;
        const newExercises = [...w.exercises];
        const currentEx = newExercises[exerciseIndex];
        const nextEx = newExercises[exerciseIndex + 1];

        if (currentEx.supersetId) {
          currentEx.supersetId = undefined;
        } else {
          const sid = `ss_${Date.now()}`;
          currentEx.supersetId = sid;
          if (nextEx) nextEx.supersetId = sid;
        }
        return { ...w, exercises: newExercises };
      });
      return { ...prev, workouts: newWorkouts };
    });
  };

  const filteredLibrary = globalExercises.filter(ex => 
    ex.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ex.muscleGroup.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="main-content">
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-xl)' }}>
        <div>
          <button onClick={() => navigate('/coach')} style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginBottom: '4px' }}>← Back</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input 
              value={program.title}
              onChange={(e) => !program.isLocked && setProgram({ ...program, title: e.target.value })}
              readOnly={program.isLocked}
              placeholder="Program Title..."
              style={{ background: 'transparent', border: 'none', color: 'var(--accent-primary)', fontSize: '1.8rem', fontWeight: 900, outline: 'none' }}
            />
            {program.isLocked && <span style={{ background: 'var(--bg-tertiary)', color: 'var(--status-yellow)', fontSize: '0.6rem', padding: '2px 6px', borderRadius: '4px', fontWeight: 800 }}>LOCKED</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button onClick={handleSave} isLoading={isSaving}>SAVE</Button>
        </div>
      </header>

      {program.isLocked && (
        <Card variant="glass" padding="var(--spacing-sm)" style={{ marginBottom: 'var(--spacing-lg)', border: '1px solid var(--status-yellow)' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--status-yellow)', textAlign: 'center' }}>
            🔒 Corporate Program: Structure and exercises cannot be modified.
          </p>
        </Card>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)' }}>
        {program.workouts.map((workout) => (
          <Card key={workout.id} style={{ borderLeft: '4px solid var(--accent-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
              <input 
                value={workout.title}
                readOnly={program.isLocked}
                onChange={(e) => setProgram({
                  ...program,
                  workouts: program.workouts.map(w => w.id === workout.id ? { ...w, title: e.target.value } : w)
                })}
                style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.2rem', fontWeight: 800, outline: 'none' }}
              />
              {!program.isLocked && <button onClick={() => setProgram(prev => ({ ...prev, workouts: prev.workouts.filter(w => w.id !== workout.id) }))} style={{ color: 'var(--status-red)', fontSize: '0.7rem' }}>Delete Day</button>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
              {workout.exercises.map((ex, idx) => {
                const isLinkedToNext = ex.supersetId && workout.exercises[idx + 1]?.supersetId === ex.supersetId;
                const isSuperset = !!ex.supersetId;
                return (
                  <div key={ex.id} style={{ position: 'relative' }}>
                    <div className={`exercise-grid ${isSuperset ? 'superset' : ''}`}>
                      <input 
                        value={ex.name} 
                        readOnly={program.isLocked} 
                        onChange={(e) => updateExercise(workout.id, ex.id, { name: e.target.value })} 
                        className="exercise-field" 
                      />
                      <div style={{ textAlign: 'center' }}>
                        <div className="exercise-label">SETS</div>
                        <input 
                          type="number" 
                          value={ex.sets} 
                          readOnly={program.isLocked} 
                          onChange={(e) => updateExercise(workout.id, ex.id, { sets: parseInt(e.target.value) || 0 })} 
                          className="exercise-field center" 
                        />
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div className="exercise-label">REPS</div>
                        <input 
                          value={ex.reps} 
                          onChange={(e) => updateExercise(workout.id, ex.id, { reps: e.target.value })} 
                          className="exercise-field center" 
                        />
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div className="exercise-label">REST</div>
                        <input 
                          type="number" 
                          value={ex.restSeconds} 
                          readOnly={program.isLocked} 
                          onChange={(e) => updateExercise(workout.id, ex.id, { restSeconds: parseInt(e.target.value) || 0 })} 
                          className="exercise-field center" 
                        />
                      </div>
                      {!program.isLocked ? (
                        <button onClick={() => removeExercise(workout.id, ex.id)} style={{ color: 'var(--status-red)', fontSize: '1.1rem', lineHeight: 1 }}>×</button>
                      ) : <div />}
                    </div>
                    {idx < workout.exercises.length - 1 && !program.isLocked && (
                      <button 
                        onClick={() => toggleSuperset(workout.id, idx)} 
                        className={`superset-toggle ${isLinkedToNext ? 'linked' : 'unlinked'}`}
                      >
                        {isLinkedToNext ? '🔗' : '+'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {!program.isLocked && (
              activeWorkoutId === workout.id ? (
                <div style={{ background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-md)', marginTop: 'var(--spacing-md)' }}>
                  <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search exercise library..." autoFocus />
                  <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: 'var(--spacing-sm)' }}>
                    {filteredLibrary.map(ex => (
                      <div key={ex.id} onClick={() => snapExercise(workout.id, ex)} style={{ padding: 'var(--spacing-sm)', borderBottom: '1px solid var(--bg-secondary)', cursor: 'pointer' }}>
                        <p style={{ fontWeight: 700, fontSize: '0.9rem' }}>{ex.name}</p>
                        <p className="text-muted" style={{ fontSize: '0.7rem' }}>{ex.muscleGroup} • {ex.equipment}</p>
                      </div>
                    ))}
                  </div>
                  <Button variant="glass" size="sm" fullWidth onClick={() => setActiveWorkoutId(null)} style={{ marginTop: 'var(--spacing-sm)' }}>Close</Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setActiveWorkoutId(workout.id)}>+ Add Exercise</Button>
              )
            )}
          </Card>
        ))}

        {!program.isLocked && (
          <Button variant="glass" onClick={addWorkout} style={{ border: '2px dashed var(--bg-tertiary)' }}>
            + ADD WORKOUT DAY
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProgramEditor;
